import { persistOperationalChat, reminderPresentation } from './operationalChat';

const normalize = value => (value || '').toLocaleLowerCase('pt-BR').normalize('NFD').replace(/[\u0300-\u036f]/g, '');

const WEEKDAYS = {
  domingo: 0,
  segunda: 1,
  terca: 2,
  quarta: 3,
  quinta: 4,
  sexta: 5,
  sabado: 6,
};

function nextWeekday(date, weekday, hour, minute) {
  const candidate = new Date(date);
  const delta = (weekday - candidate.getDay() + 7) % 7;
  candidate.setDate(candidate.getDate() + delta);
  candidate.setHours(hour, minute, 0, 0);
  // "segunda às 9h" em uma segunda depois das 9h significa a próxima,
  // nunca um compromisso no passado.
  if (candidate.getTime() <= Date.now()) candidate.setDate(candidate.getDate() + 7);
  return candidate;
}

/**
 * Um aviso no dia anterior é o padrão da Athena. O alerta de 15 minutos é
 * complementar e cobre compromissos criados com menos de um dia de distância.
 */
export function defaultReminderMinutesFor(startAt) {
  const startsAt = Date.parse(startAt);
  if (!Number.isFinite(startsAt)) return [15];
  return startsAt - Date.now() > 24 * 60 * 60 * 1000 ? [1440, 15] : [15];
}

// Interpreta apenas pedidos explícitos de agenda. A criação continua limitada a
// eventos do usuário autenticado e não executa nenhuma outra ação externa.
export function parseCalendarChatRequest(message) {
  const raw = (message || '').trim();
  const text = normalize(raw);
  if (!/\b(agende|agendar|agenda|marque|marcar|marca|crie\s+(?:um\s+)?evento|adicione\s+(?:na\s+)?agenda)\b/.test(text)) return null;
  // Aceita os jeitos naturais "9h", "9h30" e "9:30". Antes, o `h`
  // sem minutos ficava preso à fronteira da palavra e a criação parava aqui.
  // A follow-up such as “15h” is merged by the shared action orchestrator
  // into “marca reunião amanhã às 15h”. Accept both that form and a compact
  // standalone `15h` without mistaking dates like 25/08 for a time.
  const time = text.match(/\b(?:as|a)\s*(\d{1,2})(?:\s*h\s*(\d{2})?|:(\d{2}))?\b/)
    || text.match(/\b(\d{1,2})\s*h\s*(\d{0,2})\b/);
  if (!time) return { incomplete: true, message: 'Qual horário você quer para esse evento? Por exemplo: “agende reunião amanhã às 15h”.' };
  const hour = Number(time[1]); const minute = Number(time[2] || time[3] || 0);
  if (hour > 23 || minute > 59) return { incomplete: true, message: 'Não entendi esse horário. Pode informar entre 00:00 e 23:59?' };
  const now = new Date(); const date = new Date(now); let foundDate = false;
  if (/\bamanha\b/.test(text)) { date.setDate(date.getDate() + 1); foundDate = true; }
  else if (/\bhoje\b/.test(text)) foundDate = true;
  else {
    const numeric = text.match(/\b(\d{1,2})\/(\d{1,2})(?:\/(\d{2,4}))?\b/);
    const weekday = Object.entries(WEEKDAYS).find(([name]) => new RegExp(`\\b${name}(?:-feira)?\\b`).test(text));
    if (numeric) {
      const day = Number(numeric[1]); const month = Number(numeric[2]) - 1;
      const year = numeric[3] ? Number(numeric[3].length === 2 ? `20${numeric[3]}` : numeric[3]) : date.getFullYear();
      date.setFullYear(year, month, day);
      // Date normaliza automaticamente 31/02; isso não é um compromisso válido.
      if (date.getFullYear() !== year || date.getMonth() !== month || date.getDate() !== day) return { incomplete: true, message: 'Essa data não existe. Pode informar o dia novamente?' };
      if (!numeric[3]) {
        date.setHours(hour, minute, 0, 0);
        if (date.getTime() <= Date.now()) date.setFullYear(year + 1);
      }
      foundDate = true;
    } else if (weekday) {
      const weekdayTitle = raw.replace(/^(?:por favor,?\s*)?(?:agende|agendar|agenda|marque|marcar|marca|crie\s+(?:um\s+)?evento|adicione\s+(?:na\s+)?agenda)\s*/i, '').replace(/\b(?:na|no)\s+(?:domingo|segunda(?:-feira)?|terça(?:-feira)?|terca(?:-feira)?|quarta(?:-feira)?|quinta(?:-feira)?|sexta(?:-feira)?|sábado|sabado)/ig, '').replace(/(?:domingo|segunda(?:-feira)?|terça(?:-feira)?|terca(?:-feira)?|quarta(?:-feira)?|quinta(?:-feira)?|sexta(?:-feira)?|sábado|sabado)/ig, '').replace(/(?:às|as)\s*\d{1,2}(?:\s*h\s*\d{0,2}|:\d{2})?/ig, '').replace(/\s+/g, ' ').replace(/^[,;:\-–—\s]+|[,;:\-–—\s]+$/g, '');
      if (weekdayTitle.length < 2) return { incomplete: true, message: 'Qual é o nome do compromisso que devo adicionar ao calendário?' };
      return { title: weekdayTitle.slice(0, 160), start_at: nextWeekday(now, weekday[1], hour, minute).toISOString() };
    }
  }
  if (!foundDate) return { incomplete: true, message: 'Em qual dia devo agendar? Você pode dizer “hoje”, “amanhã” ou uma data como 25/08.' };
  date.setHours(hour, minute, 0, 0);
  if (date.getTime() <= Date.now() && /\bhoje\b/.test(text)) return { incomplete: true, message: 'Esse horário de hoje já passou. Quer escolher outro horário ou agendar para amanhã?' };
  const title = raw.replace(/^(?:por favor,?\s*)?(?:agende|agendar|agenda|marque|marcar|marca|crie\s+(?:um\s+)?evento|adicione\s+(?:na\s+)?agenda)\s*/i, '').replace(/\b(?:para|no\s+dia|na|no)\s+(?:hoje|amanhã|amanha|domingo|segunda(?:-feira)?|terça(?:-feira)?|terca(?:-feira)?|quarta(?:-feira)?|quinta(?:-feira)?|sexta(?:-feira)?|sábado|sabado|\d{1,2}\/\d{1,2}(?:\/\d{2,4})?)/ig, '').replace(/(?:hoje|amanhã|amanha)/ig, '').replace(/(?:domingo|segunda(?:-feira)?|terça(?:-feira)?|terca(?:-feira)?|quarta(?:-feira)?|quinta(?:-feira)?|sexta(?:-feira)?|sábado|sabado)/ig, '').replace(/(?:às|as)\s*\d{1,2}(?:\s*h\s*\d{0,2}|:\d{2})?/ig, '').replace(/\d{1,2}\/\d{1,2}(?:\/\d{2,4})?/g, '').replace(/\s+/g, ' ').replace(/^[,;:\-–—\s]+|[,;:\-–—\s]+$/g, '');
  if (title.length < 2) return { incomplete: true, message: 'Qual é o nome do compromisso que devo adicionar ao calendário?' };
  return { title: title.slice(0, 160), start_at: date.toISOString() };
}

export async function createCalendarEventFromChat({ apiBase, token, request, sessionId = null, userMessage = '', projectId = null }) {
  const headers = { Authorization: `Bearer ${token}` };
  const { data: calendarsData } = await fetch(`${apiBase}/calendars`, { headers }).then(async response => ({ data: await response.json() }));
  let calendar = calendarsData.calendars?.[0];
  if (!calendar) {
    const response = await fetch(`${apiBase}/calendars`, { method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' }, body: JSON.stringify({ name: 'Pessoal', color: '#3B82F6', is_default: true }) });
    const data = await response.json(); if (!response.ok) throw new Error(data.error || 'Não consegui criar o calendário padrão'); calendar = data.calendar;
  }
  const reminderMinutes = defaultReminderMinutesFor(request.start_at);
  const response = await fetch(`${apiBase}/events`, { method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' }, body: JSON.stringify({ calendar_id: calendar.id, title: request.title, start_at: request.start_at, timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Fortaleza', reminder_minutes: reminderMinutes, idempotency_key: `chat-${Date.now()}-${request.title}` }) });
  const data = await response.json(); if (!response.ok) throw new Error(data.error || 'Não consegui agendar agora');
  const when = new Intl.DateTimeFormat('pt-BR', { dateStyle: 'full', timeStyle: 'short' }).format(new Date(request.start_at));
  const scheduled = Number(data.reminders_scheduled || 0);
  const reminderCopy = scheduled === 0
    ? 'Como esse horário está muito próximo, não há tempo hábil para criar um novo lembrete.'
    : reminderMinutes.includes(1440)
      ? 'Vou te lembrar no dia anterior e 15 minutos antes.'
      : 'Vou te lembrar 15 minutos antes.';
  const answer = `Pronto! Agendei “${request.title}” para ${when} no calendário ${calendar.name}. ${reminderCopy} ✨`;
  const responsePresentation = reminderPresentation({
    title: request.title,
    summary: `Compromisso agendado para ${when}.`,
    when: request.start_at,
    items: scheduled > 0
      ? [reminderCopy, 'Manter este compromisso acompanhado pela Athena.']
      : ['O compromisso está no calendário.', 'Definir um lembrete manual se quiser um aviso antes.'],
  });
  const persisted = await persistOperationalChat({
    apiBase, token, sessionId, userMessage, assistantMessage: answer, projectId, responsePresentation,
  }).catch(() => null);
  return {
    answer,
    sessionId: persisted?.sessionId || null,
    responseMessageId: persisted?.responseMessageId || null,
    responsePresentation: persisted?.responsePresentation || responsePresentation,
  };
}
